import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import axios from 'axios';
import Footer from '../components/Footer';
import { useAuth } from '../App';

function Articles() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    nom: '',
    description: '',
    prix_ht: '',
    prix_ttc: '',
    tva: 21.0,
    unite: '€'
  });
  const [createForm, setCreateForm] = useState({
    nom: '',
    description: '',
    prix_ttc: '',
    tva: 21.0,
    unite: '€'
  });

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get('/api/articles');
      setArticles(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleMenuOpen = (event, article) => {
    if (article && article.id) {
      setAnchorEl(event.currentTarget);
      setSelectedArticle(article);
    } else {
      setSnackbar({ 
        open: true, 
        message: 'Erreur : Article invalide', 
        severity: 'error' 
      });
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Ne pas remettre selectedArticle à null ici pour permettre la modification
  };

  const handleEdit = () => {
    if (selectedArticle && selectedArticle.id) {
      // Calculer le prix TTC à partir du prix HT existant
      const prixHT = selectedArticle.prix_ht || 0;
      const prixTTC = prixHT * (1 + (selectedArticle.tva || 21.0) / 100);
      
      setEditForm({
        nom: selectedArticle.nom || '',
        description: selectedArticle.description || '',
        prix_ht: prixHT.toFixed(2),
        prix_ttc: prixTTC.toFixed(2), // Afficher le prix TTC calculé
        tva: selectedArticle.tva || 21.0,
        unite: selectedArticle.unite || '€'
      });
      setEditDialogOpen(true);
      setAnchorEl(null); // Fermer le menu
    } else {
      setSnackbar({ 
        open: true, 
        message: 'Erreur : Impossible de modifier cet article', 
        severity: 'error' 
      });
      setAnchorEl(null); // Fermer le menu
    }
  };

  const handleDelete = async () => {
    if (!selectedArticle || !selectedArticle.id) {
      setSnackbar({ 
        open: true, 
        message: 'Erreur : Article non sélectionné', 
        severity: 'error' 
      });
      setAnchorEl(null);
      return;
    }

    try {
      await axios.delete(`/api/articles/${selectedArticle.id}`);
      setSnackbar({ open: true, message: 'Article supprimé avec succès', severity: 'success' });
      setSelectedArticle(null); // Remettre à null après suppression
      setAnchorEl(null);
      fetchArticles();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Erreur lors de la suppression', 
        severity: 'error' 
      });
      setAnchorEl(null);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    // Mettre à jour le formulaire avec la nouvelle valeur
    setEditForm(prev => {
      const updatedForm = {
        ...prev,
        [name]: value
      };

      // Calculer le prix HT si le prix TTC est modifié et n'est pas vide
      if (name === 'prix_ttc' && value && value !== '' && !isNaN(parseFloat(value))) {
        const prixTTC = parseFloat(value);
        const tva = parseFloat(updatedForm.tva);
        if (!isNaN(prixTTC) && !isNaN(tva) && tva > 0) {
          const prixHT = prixTTC / (1 + tva / 100);
          updatedForm.prix_ht = prixHT.toFixed(2);
        }
      }

      // Recalculer le prix HT si la TVA change et qu'un prix TTC existe
      if (name === 'tva' && updatedForm.prix_ttc && updatedForm.prix_ttc !== '') {
        const prixTTC = parseFloat(updatedForm.prix_ttc);
        const tva = parseFloat(value);
        if (!isNaN(prixTTC) && !isNaN(tva) && tva > 0) {
          const prixHT = prixTTC / (1 + tva / 100);
          updatedForm.prix_ht = prixHT.toFixed(2);
        }
      }

      return updatedForm;
    });
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async () => {
    try {
      // Vérifier que selectedArticle existe
      if (!selectedArticle || !selectedArticle.id) {
        setSnackbar({ 
          open: true, 
          message: 'Erreur : Article non sélectionné', 
          severity: 'error' 
        });
        return;
      }

      // Validation des champs obligatoires
      if (!editForm.nom || editForm.nom.trim() === '') {
        setSnackbar({ 
          open: true, 
          message: 'Le nom de l\'article est obligatoire', 
          severity: 'error' 
        });
        return;
      }

      // Vérifier qu'au moins un prix est fourni
      if ((!editForm.prix_ttc || editForm.prix_ttc === '') && (!editForm.prix_ht || editForm.prix_ht === '')) {
        setSnackbar({ 
          open: true, 
          message: 'Le prix HT ou TTC est obligatoire', 
          severity: 'error' 
        });
        return;
      }

      // Validation des valeurs numériques
      const tva = parseFloat(editForm.tva);
      if (isNaN(tva) || tva < 0) {
        setSnackbar({ 
          open: true, 
          message: 'La TVA doit être un nombre positif', 
          severity: 'error' 
        });
        return;
      }

      // Préparer les données pour l'envoi
      const updateData = {
        nom: editForm.nom.trim(),
        description: editForm.description.trim(),
        tva: tva,
        unite: editForm.unite || '€'
      };

      // Si un prix TTC est fourni, l'utiliser pour calculer le prix HT
      if (editForm.prix_ttc && editForm.prix_ttc !== '') {
        const prixTTC = parseFloat(editForm.prix_ttc);
        if (isNaN(prixTTC) || prixTTC < 0) {
          setSnackbar({ 
            open: true, 
            message: 'Le prix TTC doit être un nombre positif', 
            severity: 'error' 
          });
          return;
        }
        updateData.prix_ttc = prixTTC;
      } else {
        // Sinon utiliser le prix HT existant
        const prixHT = parseFloat(editForm.prix_ht);
        if (isNaN(prixHT) || prixHT < 0) {
          setSnackbar({ 
            open: true, 
            message: 'Le prix HT doit être un nombre positif', 
            severity: 'error' 
          });
          return;
        }
        updateData.prix_ht = prixHT;
      }

      await axios.put(`/api/articles/${selectedArticle.id}`, updateData);
      setSnackbar({ open: true, message: 'Article modifié avec succès', severity: 'success' });
      setEditDialogOpen(false);
      setSelectedArticle(null); // Remettre à null après modification réussie
      fetchArticles();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || err.message || 'Erreur lors de la modification', 
        severity: 'error' 
      });
    }
  };

  const handleCreateSubmit = async () => {
    try {
      if (!createForm.nom) {
        setSnackbar({ 
          open: true, 
          message: 'Le nom de l\'article est obligatoire', 
          severity: 'error' 
        });
        return;
      }

      if (!createForm.prix_ttc || createForm.prix_ttc === '') {
        setSnackbar({ 
          open: true, 
          message: 'Le prix TTC est obligatoire', 
          severity: 'error' 
        });
        return;
      }

      const prix_ht = parseFloat(createForm.prix_ttc) / (1 + parseFloat(createForm.tva) / 100);
      const articleData = {
        nom: createForm.nom,
        description: createForm.description,
        prix_ht: prix_ht,
        tva: parseFloat(createForm.tva),
        unite: createForm.unite
      };
      
      await axios.post('/api/articles', articleData);
      setSnackbar({ open: true, message: 'Article créé avec succès', severity: 'success' });
      setCreateDialogOpen(false);
      setCreateForm({
        nom: '',
        description: '',
        prix_ttc: '',
        tva: 21.0,
        unite: '€'
      });
      fetchArticles();
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Erreur lors de la création', 
        severity: 'error' 
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedArticle(null);
  };

  if (loading) {
    return <Typography>Chargement...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Articles
        </Typography>
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nouvel Article
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Référence</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Prix TTC</TableCell>
              <TableCell>Unité</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell>{article.reference || 'N/A'}</TableCell>
                <TableCell>{article.nom || 'Sans nom'}</TableCell>
                <TableCell>{article.description || ''}</TableCell>
                <TableCell>{((article.prix_ht || 0) * (1 + (article.tva || 0) / 100)).toFixed(2)} €</TableCell>
                <TableCell>{article.unite || '€'}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, article)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu contextuel */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <>
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Modifier</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Supprimer</ListItemText>
            </MenuItem>
          </>
        )}
        {user?.role !== 'admin' && user?.role !== 'superadmin' && (
          <MenuItem disabled>
            <ListItemText>Modification réservée aux administrateurs</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Dialogue de création */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel article</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nom"
              name="nom"
              value={createForm.nom}
              onChange={handleCreateFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={createForm.description}
              onChange={handleCreateFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Prix TTC"
              name="prix_ttc"
              type="number"
              value={createForm.prix_ttc}
              onChange={handleCreateFormChange}
              margin="normal"
              required
              helperText="Le prix HT sera calculé automatiquement"
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="TVA (%)"
                name="tva"
                type="number"
                value={createForm.tva}
                onChange={handleCreateFormChange}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Unité"
                name="unite"
                value={createForm.unite}
                onChange={handleCreateFormChange}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de modification */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l'article</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nom"
              name="nom"
              value={editForm.nom}
              onChange={handleEditFormChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={editForm.description}
              onChange={handleEditFormChange}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Prix TTC"
              name="prix_ttc"
              type="number"
              value={editForm.prix_ttc}
              onChange={handleEditFormChange}
              margin="normal"
              helperText="Le prix HT sera calculé automatiquement"
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Prix HT"
                name="prix_ht"
                type="number"
                value={editForm.prix_ht}
                InputProps={{ readOnly: true }}
                helperText="Calculé automatiquement"
                sx={{ flex: 1 }}
              />
              <TextField
                label="TVA (%)"
                name="tva"
                type="number"
                value={editForm.tva}
                onChange={handleEditFormChange}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Unité"
                name="unite"
                value={editForm.unite}
                onChange={handleEditFormChange}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>
            Annuler
          </Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default Articles; 